import React from 'react';
import { Checkbox, Button, Space, Typography, Spin } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faTimes, faSearch, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../../../context/ThemeContext';
import {
  StyledCard,
  ItemCard,
  LoadingSpinnerContainer,
  EmptyStateContainer,
  CursorPointerText,
  TitleNoMargin,
  SearchIcon,
  InputMarginBottom,
  FullWidthSpaceSmall,
  FlexSpace,
  SecondaryTextSmall,
  StyledImage,
} from '../Styles';
import { AddToItemsProps } from '../types';
import { MESSAGES, PLACEHOLDER_IMAGES } from '../constants';
import { useAddToItems } from '../hooks/useAddToItems';
import { getMediaURL } from '../../../../context/API';

const { Text } = Typography;

const AddToItems: React.FC<AddToItemsProps> = ({
  customizationData,
  handleBackClick,
  handleSubmit,
  handleRelatedItemsChange,
  width,
}) => {
  const { isDarkMode } = useTheme();
  const {
    relatedItems,
    searchQuery,
    isSearching,
    filteredCategories,
    selectedCategory,
    categoryItems,
    loadingCategories,
    loadingItems,
    isSaving,
    handleSearchChange,
    handleSearchFocus,
    handleCategoryClick,
    handleRemoveItem,
    handleCheckboxChange,
    saveChanges,
    clearCategory,
  } = useAddToItems(customizationData, handleSubmit, handleRelatedItemsChange);

  const isFixed = !width;

  return (
    <StyledCard $width={width} $isFixed={isFixed}>
      <div style={{ flexShrink: 0, padding: 16 }}>
        <Space>
          <Button
            type="text"
            icon={<FontAwesomeIcon icon={faChevronLeft} />}
            onClick={handleBackClick}
            size="small"
          />
          <CursorPointerText onClick={handleBackClick}>Customizations</CursorPointerText>
          <Text> / Add to items</Text>
        </Space>
        <TitleNoMargin level={4} style={{ marginTop: 12, marginBottom: 4 }}>Add to Items</TitleNoMargin>
        <Text type="secondary">Select which items these customizations will be added to.</Text>
        <InputMarginBottom
          style={{ marginTop: 12 }}
          placeholder="Search all items or categories"
          prefix={<SearchIcon><FontAwesomeIcon icon={faSearch} /></SearchIcon>}
          value={searchQuery}
          onChange={handleSearchChange}
          onFocus={handleSearchFocus}
        />
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px', minHeight: 0 }}>
        {loadingCategories ? (
          <LoadingSpinnerContainer>
            <Spin size="large" tip={MESSAGES.LOADING_CATEGORIES} />
          </LoadingSpinnerContainer>
        ) : selectedCategory ? (
          loadingItems ? (
            <LoadingSpinnerContainer>
              <Spin size="large" tip={MESSAGES.LOADING_ITEMS} />
            </LoadingSpinnerContainer>
          ) : categoryItems.length > 0 ? (
            <FullWidthSpaceSmall direction="vertical" size="small">
              <Button type="text" icon={<FontAwesomeIcon icon={faChevronLeft} />} onClick={clearCategory} size="small" style={{ paddingLeft: 0, marginBottom: 4 }}>
                Back to categories
              </Button>
              {categoryItems.map((item) => (
                <ItemCard key={item.id} $isDark={isDarkMode}>
                  <Space>
                    <Checkbox checked={item.isChecked} onChange={() => handleCheckboxChange(item)} />
                    <StyledImage width={50} height={50} src={item.picture} alt={item.item} fallback={PLACEHOLDER_IMAGES.ITEM} />
                    <div>
                      <Text strong>{item.item}</Text>
                      <div>
                        <SecondaryTextSmall type="secondary">
                          ${item.price != null ? item.price : ''}
                        </SecondaryTextSmall>
                      </div>
                    </div>
                  </Space>
                </ItemCard>
              ))}
            </FullWidthSpaceSmall>
          ) : (
            <EmptyStateContainer>
              <Button type="text" icon={<FontAwesomeIcon icon={faChevronLeft} />} onClick={clearCategory} size="small" style={{ paddingLeft: 0, marginBottom: 8 }}>
                Back to categories
              </Button>
              <Text type="secondary">{MESSAGES.NO_ITEMS_FOUND}</Text>
            </EmptyStateContainer>
          )
        ) : (isSearching || relatedItems.length === 0) ? (
          filteredCategories.length > 0 ? (
            <FullWidthSpaceSmall direction="vertical" size="small">
              {filteredCategories.map((category) => (
                <ItemCard key={category.id} $isDark={isDarkMode} onClick={() => handleCategoryClick(category.id)}>
                  <FlexSpace>
                    <TitleNoMargin level={5}>{category.category}</TitleNoMargin>
                    <FontAwesomeIcon icon={faChevronRight} />
                  </FlexSpace>
                </ItemCard>
              ))}
            </FullWidthSpaceSmall>
          ) : (
            <EmptyStateContainer>
              <Text type="secondary">
                {searchQuery ? MESSAGES.NO_CATEGORIES_MATCHING_SEARCH : MESSAGES.NO_CATEGORIES_AVAILABLE}
              </Text>
            </EmptyStateContainer>
          )
        ) : (
          <FullWidthSpaceSmall direction="vertical" size="small">
            {relatedItems.map((relatedItem) => {
              const { item } = relatedItem;
              const hasItem = item && (item as any).id;
              return (
                <ItemCard key={relatedItem.id} $isDark={isDarkMode}>
                  <FlexSpace>
                    <Space>
                      {hasItem ? (
                        <>
                          <StyledImage
                            width={50} height={50}
                            src={getMediaURL((item as any).picture) || PLACEHOLDER_IMAGES.ITEM}
                            alt={(item as any).item || 'Unnamed Item'}
                            fallback={PLACEHOLDER_IMAGES.ITEM}
                          />
                          <div>
                            <Text strong>{(item as any).item || 'Unnamed Item'}</Text>
                            <div>
                              <SecondaryTextSmall type="secondary">
                                {(item as any).price != null ? `$${(item as any).price}` : 'Price not available'}
                              </SecondaryTextSmall>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <StyledImage width={50} height={50} src={PLACEHOLDER_IMAGES.ITEM} alt="Unnamed Item" fallback={PLACEHOLDER_IMAGES.ITEM} />
                          <div>
                            <Text strong>Unnamed Item</Text>
                            <div><SecondaryTextSmall type="secondary">Price not available</SecondaryTextSmall></div>
                          </div>
                        </>
                      )}
                    </Space>
                    {hasItem && (
                      <Button type="text" danger icon={<FontAwesomeIcon icon={faTimes} />} onClick={() => handleRemoveItem((item as any).id)} size="small" />
                    )}
                  </FlexSpace>
                </ItemCard>
              );
            })}
          </FullWidthSpaceSmall>
        )}
      </div>

      {selectedCategory !== null && (
        <div style={{ flexShrink: 0, padding: '12px 16px 16px', borderTop: `1px solid ${isDarkMode ? '#303030' : '#f0f0f0'}`, display: 'flex', justifyContent: 'flex-end', background: isDarkMode ? '#1a1a2e' : '#fff' }}>
          <Button type="primary" onClick={saveChanges} loading={isSaving} style={{ height: 40, paddingInline: 24, backgroundColor: '#2D6CCA', borderColor: '#2D6CCA' }}>
            Save
          </Button>
        </div>
      )}
    </StyledCard>
  );
};

export default React.memo(AddToItems);
